import { useState } from 'react';
import { Button, Card, Spinner } from './ui';
import { Dialog } from './dialogs/Dialog';
import { SubscriptionsAPI, type GoogleReconcileChange } from '../services/endpoints/admin';
import { useDispatchAction } from '../hooks/useDispatchAction';
import { formatDateTime } from '../utils/format';

interface Sweep {
  applied: boolean;
  total: number;
  counts: Record<string, number>;
  changes: GoogleReconcileChange[];
  errors: { subscriptionId: string; message: string }[];
}

const COUNT_LABELS: [string, string][] = [
  ['checked', 'Checked'],
  ['changed', 'Changed'],
  ['unchanged', 'Unchanged'],
  ['goneFromPlay', 'Gone from Play'],
  ['noSubscription', 'No subscription row'],
  ['error', 'Errors'],
];

const MAX_ROWS = 200;

/**
 * Re-reads every Google Play subscription from Google and corrects expiry / cancelled on the rows
 * that drifted — the recovery for RTDN notifications lost while the push endpoint was dead.
 * Preview walks every page without writing; Apply walks them again and saves.
 */
export function GooglePlayReconcile() {
  const [sweep, setSweep] = useState<Sweep | null>(null);
  const [progress, setProgress] = useState<{ done: number; total: number } | null>(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const { run, busy } = useDispatchAction();

  async function walk(apply: boolean): Promise<Sweep> {
    const acc: Sweep = { applied: apply, total: 0, counts: {}, changes: [], errors: [] };
    let offset: number | null = 0;
    while (offset !== null) {
      const page = await SubscriptionsAPI.reconcileGoogle(apply, offset);
      acc.total = page.total;
      for (const [k, v] of Object.entries(page.counts)) acc.counts[k] = (acc.counts[k] || 0) + v;
      acc.changes.push(...page.changes);
      acc.errors.push(...page.errors);
      offset = page.nextOffset;
      setProgress({ done: offset ?? page.total, total: page.total });
    }
    return acc;
  }

  async function start(apply: boolean) {
    setConfirmOpen(false);
    setProgress(null);
    const result = await run(apply ? 'Apply Google reconcile' : 'Preview Google reconcile', () => walk(apply));
    if (result) setSweep(result);
    setProgress(null);
  }

  const previewed = sweep && !sweep.applied;

  return (
    <Card style={{ marginTop: 24 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 16, flexWrap: 'wrap' }}>
        <div style={{ maxWidth: 620 }}>
          <div className="nb-cache__eyebrow">STORE · GOOGLE PLAY</div>
          <div className="nb-cache__name">Reconcile subscriptions</div>
          <p className="nb-prose-italic" style={{ marginTop: 8 }}>
            Asks Google for the current state of every Play subscription and fixes the expiry date and
            cancelled (won't renew) flag where ours differ. Run Preview first — it writes nothing.
            Apply saves the changes, clears each changed subscription's cache, and is recorded in the audit log.
          </p>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <Button onClick={() => start(false)} disabled={busy}>
            {busy && !confirmOpen ? <Spinner /> : 'Preview'}
          </Button>
          <Button tone="ember" onClick={() => setConfirmOpen(true)} disabled={busy || !previewed}>
            Apply
          </Button>
        </div>
      </div>

      {progress && (
        <div className="nb-cache__notice" style={{ marginTop: 16 }}>
          <Spinner /> Checked {progress.done.toLocaleString()} of {progress.total.toLocaleString()}…
        </div>
      )}

      {sweep && (
        <div style={{ marginTop: 20 }}>
          <div className="nb-cache__eyebrow">{sweep.applied ? 'APPLIED' : 'PREVIEW — NOTHING WRITTEN'}</div>
          <dl className="nb-cache__meta" style={{ maxWidth: 420 }}>
            {COUNT_LABELS.map(([key, label]) => (
              <div key={key}>
                <dt>{label}</dt>
                <dd>{(sweep.counts[key] || 0).toLocaleString()}</dd>
              </div>
            ))}
          </dl>

          {sweep.changes.length > 0 && (
            <div style={{ marginTop: 20, overflowX: 'auto' }}>
              <table className="nb-table">
                <thead>
                  <tr>
                    <th>Subscription</th>
                    <th>Play state</th>
                    <th>Expires</th>
                    <th>Cancelled</th>
                  </tr>
                </thead>
                <tbody>
                  {sweep.changes.slice(0, MAX_ROWS).map(c => (
                    <tr key={c.subscriptionId}>
                      <td className="nb-table__mono">{c.subscriptionId}</td>
                      <td className="nb-table__mono">{c.playState.replace('SUBSCRIPTION_STATE_', '')}</td>
                      <td className="nb-table__mono">
                        {c.expiresOn ? `${showDate(c.expiresOn.from)} → ${showDate(c.expiresOn.to)}` : '—'}
                      </td>
                      <td className="nb-table__mono">
                        {c.cancelled ? `${yesNo(c.cancelled.from)} → ${yesNo(c.cancelled.to)}` : '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {sweep.changes.length > MAX_ROWS && (
                <p className="nb-prose-italic">Showing the first {MAX_ROWS} of {sweep.changes.length.toLocaleString()} changes.</p>
              )}
            </div>
          )}

          {sweep.errors.length > 0 && (
            <div className="nb-cache__notice" style={{ marginTop: 16 }}>
              <strong>{sweep.errors.length} could not be checked.</strong>
              {sweep.errors.slice(0, 20).map(e => (
                <div key={e.subscriptionId}><code>{e.subscriptionId}</code> {e.message}</div>
              ))}
            </div>
          )}
        </div>
      )}

      <Dialog
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        title="Apply Google reconcile"
        footer={
          <>
            <Button onClick={() => setConfirmOpen(false)}>Cancel</Button>
            <Button tone="ember" onClick={() => start(true)}>Apply changes</Button>
          </>
        }
      >
        <p className="nb-prose-italic">
          The preview found {sweep?.counts.changed ?? 0} subscription(s) to change. Apply re-checks each one
          with Google and saves what Google reports now, so the final count can differ slightly.
        </p>
      </Dialog>
    </Card>
  );
}

function showDate(v: string) {
  return v && v !== 'null' ? formatDateTime(v) : 'none';
}

function yesNo(v: boolean) {
  return v ? 'yes' : 'no';
}
